import { useQuery } from '@apollo/client';
import LoadingPage from '@website/components/utilities/LoadingPage';
import NotFound from '@website/components/utilities/NotFound';
import {
  EditTripWaypointsDocument,
  WayPointType,
  EditTripWaypointsQuery,
} from '@website/types/graphqlOperations';
import { useState, useMemo } from 'react';
import { Button, Card } from 'react-bootstrap';
import { MdCancel } from 'react-icons/md';
import { DateTime } from 'luxon';
import { DeleteWayPoint } from './DeleteWayPoint';
import WaypointEditForm from './WaypointEditForm';

type Props = {
  tripID: string;
};

export default function EditWayPoints({ tripID }: Props) {
  const [isAddFormShown, setIsAddFormShown] = useState(false);

  const { data, loading, error } = useQuery(EditTripWaypointsDocument, {
    variables: { tripID },
    fetchPolicy: 'cache-and-network',
  });

  const sorted = useMemo<
    EditTripWaypointsQuery['trip']['wayPoints']['items']
  >(() => {
    if (!data) return [];

    return [...data.trip.wayPoints.items].sort((a, b) => {
      // Check if the types are different
      if (a.type !== b.type) {
        if (a.type === WayPointType.MeetPoint) return -1;
        if (b.type === WayPointType.MeetPoint) return 1;
      }

      // Check if the days are different
      if (a.itineraryItemID !== b.itineraryItemID) {
        const aDay = data.trip.itinerary.items.findIndex(
          (i) => i.id === a.itineraryItemID,
        );
        const bDay = data.trip.itinerary.items.findIndex(
          (i) => i.id === b.itineraryItemID,
        );

        return aDay - bDay;
      }

      // Otherwise sort by time
      return DateTime.fromISO(a.arriveAt.localTime) <
        DateTime.fromISO(b.arriveAt.localTime)
        ? -1
        : 1;
    });
  }, [data]);

  if (loading && !data) {
    return <LoadingPage />;
  }

  if (error && !data) {
    return <NotFound />;
  }

  return (
    <>
      {sorted.map((waypoint) => (
        <Card key={waypoint.id} className="my-2">
          <Card.Body>
            <DeleteWayPoint wayPointID={waypoint.id} />
            <WaypointEditForm
              waypoint={waypoint}
              itineraryItems={data.trip.itinerary.items}
              trip={data.trip}
            />
          </Card.Body>
        </Card>
      ))}
      {isAddFormShown ? (
        <Card className="my-2">
          <Card.Body>
            <Button
              aria-label="Cancel"
              variant="danger"
              className="float-end mb-2 p-0 px-2 pb-1"
              onClick={() => setIsAddFormShown(false)}
            >
              <MdCancel /> Cancel
            </Button>
            <WaypointEditForm
              itineraryItems={data.trip.itinerary.items}
              trip={data.trip}
              key={'NewItem'}
              onSuccess={() => setIsAddFormShown(false)}
            />
          </Card.Body>
        </Card>
      ) : (
        <div className="d-grid gap-2">
          <Button onClick={() => setIsAddFormShown(true)} className="mt-2">
            + Add Meet Point
          </Button>
        </div>
      )}
    </>
  );
}
